/**
 * New Claim Page - Create a new reimbursement claim with expense items
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { reimbursementApi } from '../api/reimbursementApi'
import type { CreateClaimInput } from '../types/reimbursement.types'
import LoadingPage from '@/shared/components/LoadingPage'

interface ExpenseItem {
  id: string
  category_id: string
  expense_date: string
  description: string
  merchant_name: string
  amount: string
  receipts: File[]
}

const generateTempId = () => `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

const formatCurrency = (amount: number, currency: string = 'IDR') => {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
  }).format(amount)
}

const NewClaimPage = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const currentDate = new Date()

  // Form state
  const [claimData, setClaimData] = useState<CreateClaimInput>({
    title: '',
    description: '',
    period_month: currentDate.getMonth() + 1,
    period_year: currentDate.getFullYear(),
    cost_center: '',
    payment_method: 'payroll',
    payment_preference: 'next_payroll',
  })

  const [items, setItems] = useState<ExpenseItem[]>([
    {
      id: generateTempId(),
      category_id: '',
      expense_date: '',
      description: '',
      merchant_name: '',
      amount: '',
      receipts: [],
    },
  ])

  // Fetch expense categories
  const { data: categoriesData, isLoading: categoriesLoading } = useQuery({
    queryKey: ['expenseCategories'],
    queryFn: reimbursementApi.getCategories,
  })

  const categories = categoriesData?.data || []

  // Create and submit claim mutation (default action)
  const createAndSubmitMutation = useMutation({
    mutationFn: async (data: { claim: CreateClaimInput; items: ExpenseItem[] }) => {
      // First create the claim
      const claimResponse = await reimbursementApi.createClaim(data.claim)
      const claim = claimResponse.data

      // Then add items and upload receipts
      for (const item of data.items) {
        const itemResponse = await reimbursementApi.addClaimItem(claim.id, {
          category_id: item.category_id,
          expense_date: item.expense_date,
          description: item.description,
          merchant_name: item.merchant_name || undefined,
          amount: parseFloat(item.amount),
        })

        // Upload receipts for this item
        const createdItem = itemResponse.data
        for (const receipt of item.receipts) {
          await reimbursementApi.uploadReceipt(claim.id, createdItem.id, receipt)
        }
      }

      // Submit the claim for approval
      const submitResponse = await reimbursementApi.submitClaim(claim.id)
      return submitResponse.data
    },
    onSuccess: (claim) => {
      queryClient.invalidateQueries({ queryKey: ['reimbursementClaims'] })
      toast.success('Claim submitted successfully')
      navigate(`/reimbursement/claims/${claim.id}`)
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error?.message || 'Failed to submit claim'
      toast.error(message)
    },
  })

  // Save as draft mutation (creates without submitting)
  const saveDraftMutation = useMutation({
    mutationFn: async (data: { claim: CreateClaimInput; items: ExpenseItem[] }) => {
      // First create the claim
      const claimResponse = await reimbursementApi.createClaim(data.claim)
      const claim = claimResponse.data

      // Then add items and upload receipts
      for (const item of data.items) {
        const itemResponse = await reimbursementApi.addClaimItem(claim.id, {
          category_id: item.category_id,
          expense_date: item.expense_date,
          description: item.description,
          merchant_name: item.merchant_name || undefined,
          amount: parseFloat(item.amount),
        })

        // Upload receipts for this item
        const createdItem = itemResponse.data
        for (const receipt of item.receipts) {
          await reimbursementApi.uploadReceipt(claim.id, createdItem.id, receipt)
        }
      }

      return claim
    },
    onSuccess: (claim) => {
      queryClient.invalidateQueries({ queryKey: ['reimbursementClaims'] })
      toast.success('Claim saved as draft')
      navigate(`/reimbursement/claims/${claim.id}`)
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error?.message || 'Failed to save draft'
      toast.error(message)
    },
  })

  const handleClaimChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target
    setClaimData((prev) => ({
      ...prev,
      [name]: name === 'period_month' || name === 'period_year' ? parseInt(value) : value,
    }))
  }

  const handleItemChange = (
    itemId: string,
    field: keyof ExpenseItem,
    value: string
  ) => {
    setItems((prev) =>
      prev.map((item) => (item.id === itemId ? { ...item, [field]: value } : item))
    )
  }

  const addItem = () => {
    setItems((prev) => [
      ...prev,
      {
        id: generateTempId(),
        category_id: '',
        expense_date: '',
        description: '',
        merchant_name: '',
        amount: '',
        receipts: [],
      },
    ])
  }

  const handleReceiptChange = (itemId: string, files: FileList | null) => {
    if (!files) return
    const fileArray = Array.from(files)
    setItems((prev) =>
      prev.map((item) =>
        item.id === itemId
          ? { ...item, receipts: [...item.receipts, ...fileArray] }
          : item
      )
    )
  }

  const removeReceipt = (itemId: string, fileIndex: number) => {
    setItems((prev) =>
      prev.map((item) =>
        item.id === itemId
          ? { ...item, receipts: item.receipts.filter((_, i) => i !== fileIndex) }
          : item
      )
    )
  }

  const removeItem = (itemId: string) => {
    if (items.length === 1) {
      toast.error('At least one expense item is required')
      return
    }
    setItems((prev) => prev.filter((item) => item.id !== itemId))
  }

  const calculateTotal = () => {
    return items.reduce((sum, item) => {
      const amount = parseFloat(item.amount) || 0
      return sum + amount
    }, 0)
  }

  const validateForm = () => {
    if (!claimData.title.trim()) {
      toast.error('Please enter a claim title')
      return false
    }

    for (let i = 0; i < items.length; i++) {
      const item = items[i]
      if (!item.category_id) {
        toast.error(`Please select a category for item ${i + 1}`)
        return false
      }
      if (!item.expense_date) {
        toast.error(`Please enter a date for item ${i + 1}`)
        return false
      }
      if (!item.description.trim()) {
        toast.error(`Please enter a description for item ${i + 1}`)
        return false
      }
      if (!item.amount || parseFloat(item.amount) <= 0) {
        toast.error(`Please enter a valid amount for item ${i + 1}`)
        return false
      }
    }

    return true
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) return

    createAndSubmitMutation.mutate({ claim: claimData, items })
  }

  const handleSaveDraft = () => {
    if (!validateForm()) return

    saveDraftMutation.mutate({ claim: claimData, items })
  }

  if (categoriesLoading) return <LoadingPage />

  const total = calculateTotal()
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ]

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">New Reimbursement Claim</h1>
        <p className="text-gray-600 mt-1">Submit your expense reimbursement request</p>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Claim Details */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Claim Details</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title *
              </label>
              <input
                type="text"
                name="title"
                value={claimData.title}
                onChange={handleClaimChange}
                placeholder="e.g., January Business Expenses"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Period Month *
              </label>
              <select
                name="period_month"
                value={claimData.period_month}
                onChange={handleClaimChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              >
                {months.map((month, index) => (
                  <option key={index} value={index + 1}>
                    {month}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Period Year *
              </label>
              <select
                name="period_year"
                value={claimData.period_year}
                onChange={handleClaimChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              >
                {[currentDate.getFullYear() - 1, currentDate.getFullYear(), currentDate.getFullYear() + 1].map(
                  (year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  )
                )}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Payment Method
              </label>
              <select
                name="payment_method"
                value={claimData.payment_method}
                onChange={handleClaimChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              >
                <option value="payroll">Payroll</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="cash">Cash</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cost Center
              </label>
              <input
                type="text"
                name="cost_center"
                value={claimData.cost_center}
                onChange={handleClaimChange}
                placeholder="e.g., CC-001"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                name="description"
                value={claimData.description}
                onChange={handleClaimChange}
                rows={2}
                placeholder="Additional notes about this claim"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Expense Items */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Expense Items</h2>
            <button
              type="button"
              onClick={addItem}
              className="px-3 py-1 text-sm bg-red-50 text-red-600 rounded-lg hover:bg-red-100"
            >
              + Add Item
            </button>
          </div>

          <div className="space-y-4">
            {items.map((item, index) => (
              <div key={item.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-sm font-medium text-gray-700">Item {index + 1}</span>
                  {items.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeItem(item.id)}
                      className="text-red-600 hover:text-red-800 text-sm"
                    >
                      Remove
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Category *</label>
                    <select
                      value={item.category_id}
                      onChange={(e) => handleItemChange(item.id, 'category_id', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                      required
                    >
                      <option value="">Select category</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Date *</label>
                    <input
                      type="date"
                      value={item.expense_date}
                      onChange={(e) => handleItemChange(item.id, 'expense_date', e.target.value)}
                      max={new Date().toISOString().split('T')[0]}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Merchant</label>
                    <input
                      type="text"
                      value={item.merchant_name}
                      onChange={(e) => handleItemChange(item.id, 'merchant_name', e.target.value)}
                      placeholder="e.g., Grab, Tokopedia"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Amount (IDR) *</label>
                    <input
                      type="number"
                      value={item.amount}
                      onChange={(e) => handleItemChange(item.id, 'amount', e.target.value)}
                      placeholder="0"
                      min="0"
                      step="1000"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                      required
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm text-gray-600 mb-1">Description *</label>
                    <input
                      type="text"
                      value={item.description}
                      onChange={(e) => handleItemChange(item.id, 'description', e.target.value)}
                      placeholder="Brief description of the expense"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                      required
                    />
                  </div>

                  {/* Receipt Upload */}
                  <div className="md:col-span-2">
                    <label className="block text-sm text-gray-600 mb-1">
                      Receipts
                      <span className="text-gray-400 font-normal ml-1">(optional)</span>
                    </label>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {item.receipts.map((file, fileIndex) => (
                        <div
                          key={fileIndex}
                          className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-lg text-sm"
                        >
                          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                          </svg>
                          <span className="truncate max-w-[150px]" title={file.name}>{file.name}</span>
                          <button
                            type="button"
                            onClick={() => removeReceipt(item.id, fileIndex)}
                            className="text-gray-400 hover:text-red-600"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                    <label className="inline-flex items-center gap-2 px-3 py-2 border border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-red-400 hover:bg-red-50 transition-colors">
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span className="text-sm text-gray-600">Attach receipt</span>
                      <input
                        type="file"
                        accept="image/*,.pdf"
                        multiple
                        className="hidden"
                        onChange={(e) => handleReceiptChange(item.id, e.target.files)}
                      />
                    </label>
                    <p className="text-xs text-gray-400 mt-1">Accepts images and PDF files</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Total */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex justify-between items-center">
              <span className="text-lg font-semibold text-gray-900">Total</span>
              <span className="text-xl font-bold text-red-600">{formatCurrency(total)}</span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate('/reimbursement')}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSaveDraft}
            disabled={saveDraftMutation.isPending || createAndSubmitMutation.isPending}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saveDraftMutation.isPending ? 'Saving...' : 'Save as Draft'}
          </button>
          <button
            type="submit"
            disabled={createAndSubmitMutation.isPending || saveDraftMutation.isPending}
            className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {createAndSubmitMutation.isPending ? 'Submitting...' : 'Submit Claim'}
          </button>
        </div>
      </form>
    </div>
  )
}

export default NewClaimPage
